import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  ExternalLink,
  Layers,
  Receipt,
  Sparkles,
  TriangleAlert,
  Wallet as WalletIcon,
} from "lucide-react";
import { z } from "zod";
import { useZodForm } from "@empite/app-kit";
import { formatMoney } from "@empite/api-client";
import type {
  CancelSubscriptionResult,
  CheckoutResult,
  Invoice,
  Money,
  PackageOffer,
  PaymentMethod,
  SubscribeResult,
  Subscription,
  Transaction,
  UsageRecord,
  Wallet as WalletPosition,
} from "@empite/api-client";
import { useCustomerApplications } from "../applications";
import type { PortalApplication } from "../applications";
import {
  pendingPoll,
  RECENT_LIMIT,
  useAlerts,
  useCancelSubscription,
  useInvoices,
  usePackages,
  useRefreshBilling,
  useStartSubscription,
  useStartTopUp,
  useSubscription,
  useTransactions,
  useUsage,
  useWallet,
} from "../billing";
import { AddCreditPanel } from "../ui/add-credit-panel";
import { AppIcon } from "../ui/app-icon";
import type { AppIdentity } from "../ui/app-identity";
import { Button } from "../ui/button";
import { Card, CardBody, CardHeader } from "../ui/card";
import { cn } from "../ui/cn";
import { CopyButton } from "../ui/copy-button";
import { DataTable } from "../ui/data-table";
import type { Column } from "../ui/data-table";
import { EmptyState } from "../ui/empty-state";
import { formatBps, formatDate, formatTimestamp, humanizeState } from "../ui/format";
import { Modal } from "../ui/modal";
import { Notice } from "../ui/notice";
import { PageHeader } from "../ui/page-header";
import { Skeleton } from "../ui/skeleton";
import { StatusPill } from "../ui/status-pill";
import {
  appIdentity,
  reservationPillTone,
  subscriptionPillTone,
  topUpPillTone,
  WalletAlertBanner,
  walletPillTone,
} from "./billing-common";
import { errorMessage, RequireSession } from "./guards";
import { ModelRatesCard } from "./model-rates";

/**
 * One application's wallet: the balance, what was added to it, what it was
 * spent on, and the one form that adds more.
 *
 * Every read on this screen is scoped to a single application id, and so is
 * the write. That is not a UI decision — it is the wallet boundary, and this
 * screen is where a customer can see that it holds. The design's two-column
 * layout says the same thing: the "Add credit" panel sits beside ONE wallet's
 * ledger, never above a list of them.
 */
export interface WalletDetailSearch {
  /** How the payment provider sent the browser back from a top-up, if it did. */
  topup?: string;
  /**
   * The same, for a subscription. A parameter of its own rather than one
   * shared `outcome`: the two flows return to the same screen and the two
   * banners say different things, and a single parameter would make a
   * subscribe that came back beside a stale `?topup=paid` unreadable.
   */
  subscribe?: string;
}

export function WalletDetail({
  applicationID,
  search,
}: {
  applicationID: string;
  search: WalletDetailSearch;
}) {
  return (
    <RequireSession>
      {() => <WalletPanels applicationID={applicationID} search={search} />}
    </RequireSession>
  );
}

function WalletPanels({
  applicationID,
  search,
}: {
  applicationID: string;
  search: WalletDetailSearch;
}) {
  const { applications } = useCustomerApplications();
  const position = useMemo(
    () => applications.findIndex((candidate) => candidate.id === applicationID),
    [applications, applicationID],
  );
  const application = position < 0 ? undefined : applications[position];
  const wallet = useWallet(applicationID);

  // The name is a nicety; the id is the authority. A wallet the customer holds
  // for an application this portal cannot name is still their wallet, so the
  // screen renders it under the id rather than refusing to show it.
  const named: PortalApplication = application ?? {
    id: applicationID,
    slug: applicationID,
    name: "This application",
    // Empty rather than a guess. This branch is the application the LIST did
    // not carry, so its publisher is precisely the thing not known here, and
    // this screen shows one wallet under a heading of its own — there is no
    // grouping for an unnamed company to sit wrong in.
    companySlug: "",
    companyName: "",
  };
  // The same position the wallets grid used, so a card and its detail screen
  // are not two different colours for one application.
  const app = appIdentity(named, position < 0 ? 0 : position);

  return (
    <>
      <PageHeader
        back={{ href: "/wallets", label: "All wallets" }}
        title={named.name}
        eyebrow={
          <div className="flex flex-wrap items-center gap-2">
            <code className="font-mono text-sm text-ink-4">
              {application ? named.slug : `Application ${applicationID}`}
            </code>
            {wallet.data ? (
              <StatusPill tone={walletPillTone(wallet.data.status)} dot>
                {wallet.data.status}
              </StatusPill>
            ) : null}
          </div>
        }
      />

      <ReturnNotice search={search} />

      {/* The same banner the wallets grid draws, scoped to this one wallet and
          reading the same cache entry, so the two screens can never disagree
          about whether this application is in trouble. No "Add credit" link on
          it: the panel that does that is already on this page. */}
      <WalletRunoutBanner applicationID={applicationID} app={app} />

      <div className="grid gap-5 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          {wallet.isPending ? (
            <Skeleton className="h-56 w-full" />
          ) : wallet.error ? (
            <Card>
              <CardHeader title="Balance" />
              <CardBody>
                <Notice tone="danger" title="We could not read this wallet">
                  {errorMessage(wallet.error)}
                </Notice>
              </CardBody>
            </Card>
          ) : (
            <BalanceCard wallet={wallet.data} app={app} application={named} />
          )}

          <PlanSection applicationID={applicationID} currency={wallet.data?.currency} />

          <TransactionsCard applicationID={applicationID} name={named.name} />
          <InvoicesCard applicationID={applicationID} name={named.name} />
          <UsageCard applicationID={applicationID} name={named.name} />
          {/* Below the ledger on purpose: a customer opens this screen to see
              what they have left, and only then asks what the next request
              will cost. */}
          <ModelRatesCard applicationID={applicationID} name={named.name} />
        </div>

        {/* The panel follows the ledger down the page on a wide screen: the
            customer reading why their balance is low is the one who wants to
            top it up, and scrolling back to the top to do it is the friction
            the design's sticky column removes. */}
        <div className="xl:sticky xl:top-24 xl:self-start">
          {wallet.isPending ? (
            <Skeleton className="h-96 w-full" />
          ) : (
            <TopUpPanel application={named} wallet={wallet.data} />
          )}
        </div>
      </div>
    </>
  );
}

/**
 * One wallet's open crossings.
 *
 * A component of its own rather than a read in `WalletPanels`, so the alert
 * query's own pending state stays next to the thing it gates: the banner must
 * not paint until this read has settled, and a parent already juggling the
 * wallet, the subscription and four tables is where that rule goes missing.
 *
 * A failed read draws nothing at all. There is no honest banner for "we could
 * not tell whether your wallet is in trouble", and the balance card above
 * already reports its own failure — a second red box saying less would only
 * bury it.
 */
function WalletRunoutBanner({ applicationID, app }: { applicationID: string; app: AppIdentity }) {
  const alerts = useAlerts(applicationID);
  const entries = useMemo(
    () => (alerts.data?.alerts ?? []).map((alert) => ({ id: alert.id, alert, app })),
    [alerts.data, app],
  );
  return (
    <WalletAlertBanner
      entries={entries}
      pending={alerts.isPending}
      linkToWallet={false}
      className="mb-5"
    />
  );
}

/**
 * What the payment provider's redirect means, said plainly.
 *
 * "Paid" is deliberately not "credited": the provider redirects the browser
 * the moment the card clears, while the credit lands when the webhook is
 * processed. Telling someone their balance has gone up before the ledger says
 * so produces a support ticket the moment they refresh.
 */
function ReturnNotice({ search }: { search: WalletDetailSearch }) {
  return (
    <>
      {/* Not "success", and not "Payment received". This banner is rendered
          from a QUERY PARAMETER — with the real Stripe the return URL is an
          ordinary link a browser follows, and it is proof of nothing at all.
          The wallet balance beside it is read from the ledger and is the only
          thing on this screen that knows whether money moved. */}
      {search.topup === "paid" ? (
        <Notice
          title="Finishing your payment"
          icon={<Sparkles className="size-4" aria-hidden />}
          className="mb-5"
        >
          Your credit appears in this wallet as soon as the payment provider confirms it. Refresh
          in a moment if the balance below has not moved yet.
        </Notice>
      ) : null}
      {search.topup === "cancelled" ? (
        <Notice title="Payment cancelled" className="mb-5">
          Nothing was charged and your balance is unchanged.
        </Notice>
      ) : null}
      {/* Not "success", and not the green tone. Coming back from the hosted
          page means the customer finished it, which is not the same as the
          payment having cleared — only the provider's webhook says that. The
          plan below carries the live state and resolves itself; this banner
          only explains where the browser has just been. */}
      {search.subscribe === "paid" ? (
        <Notice title="Finishing your subscription" className="mb-5">
          Your plan starts once the payment provider confirms the payment, which normally takes a
          few seconds. It is shown below as pending until then, and updates on its own.
        </Notice>
      ) : null}
      {search.subscribe === "cancelled" ? (
        <Notice title="Subscription not started" className="mb-5">
          Nothing was charged and you are still on the plan you were on.
        </Notice>
      ) : null}
    </>
  );
}

function BalanceCard({
  wallet,
  app,
  application,
}: {
  wallet: WalletPosition | null | undefined;
  app: AppIdentity;
  application: PortalApplication;
}) {
  if (!wallet) {
    return (
      <Card>
        <EmptyState
          icon={<WalletIcon className="size-6" aria-hidden />}
          title="No wallet yet"
          body={`Nothing has been added for ${application.name}. The first top-up opens the wallet.`}
        />
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader title="Balance" />
      <CardBody>
        <div className="flex flex-wrap items-center gap-5">
          <AppIcon initial={app.initial} tone={app.tone} size="lg" />
          <div>
            <p className="text-2xs font-bold tracking-[0.07em] text-ink-4 uppercase">Available</p>
            <p className="tnum font-display text-4xl font-bold tracking-[-0.03em] text-success-ink">
              {formatMoney(wallet.available)}
            </p>
          </div>
          <div className="sm:ml-auto">
            {/* Both figures, because a reservation moves credit between them
                without changing the total: a customer who sees only
                "available" fall reads a charge that has not happened yet. */}
            <p className="text-2xs font-bold tracking-[0.07em] text-ink-4 uppercase">
              Held in flight
            </p>
            <p className="tnum font-display text-xl font-bold text-ink">
              {formatMoney(wallet.reserved)}
            </p>
          </div>
        </div>

        <BalanceSplit wallet={wallet} />

        <dl className="mt-6 grid gap-3 border-t border-line-soft pt-5 text-xs sm:grid-cols-2">
          <div className="flex items-center justify-between gap-3 sm:justify-start sm:gap-4">
            <dt className="text-ink-3">Status</dt>
            <dd>
              <StatusPill tone={walletPillTone(wallet.status)}>{wallet.status}</StatusPill>
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3 sm:justify-end">
            <dt className="text-ink-3">Wallet</dt>
            <dd className="flex items-center gap-1">
              <code className="font-mono text-2xs break-all text-ink-2">{wallet.wallet_id}</code>
              <CopyButton value={wallet.wallet_id} label="Copy wallet id" />
            </dd>
          </div>
        </dl>

        {wallet.cache_stale ? (
          <Notice tone="warning" title="This balance may be out of date" className="mt-5">
            The cached position has not caught up with the ledger yet.
          </Notice>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * The two halves the balance above is made of.
 *
 * The total stays the headline and stays unchanged, because it is still the
 * number that answers "can I afford this" — which bucket a micro sits in does
 * not change what it can buy. The split sits beneath it because the two halves
 * behave differently: the subscription allowance is burned at the reset and
 * the top-up credit was bought outright and never expires. Both lines say so
 * in words. A bare pair of figures would leave a customer to discover on
 * Thursday that a third of their balance was never theirs to keep, and that
 * discovery arrives as a support ticket.
 *
 * A wallet that has never had a subscription has no reset to name, so it is
 * shown its top-up line alone rather than a subscription row reading "USD 0.00
 * — resets never". The bucket is still checked for money as well as the date:
 * a zero allowance with a reset date pending is a real state (this cycle's
 * credit is spent) and the customer is the one who wants the date.
 *
 * One line each, stacked, rather than a two-column pair: each label carries a
 * clause as well as a name, and side by side the reset date wraps under the
 * first label and lands beside the SECOND figure.
 */
function BalanceSplit({ wallet }: { wallet: WalletPosition }) {
  const resetsAt = wallet.subscription_resets_at;
  const subscribed = Boolean(resetsAt) || micros(wallet.subscription_credit) !== 0n;

  return (
    <dl className="mt-5 space-y-2 border-t border-line-soft pt-4 text-xs">
      {subscribed ? (
        <div className="flex items-center justify-between gap-3">
          <dt className="text-ink-3">
            Subscription credit
            <span className="text-ink-4">
              {resetsAt ? ` — resets ${formatDate(resetsAt)}` : " — resets each period"}
            </span>
          </dt>
          <dd className="tnum font-semibold whitespace-nowrap text-ink">
            {formatMoney(wallet.subscription_credit)}
          </dd>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <dt className="text-ink-3">
          Top-up credit<span className="text-ink-4"> — never expires</span>
        </dt>
        <dd className="tnum font-semibold whitespace-nowrap text-ink">
          {formatMoney(wallet.topup_credit)}
        </dd>
      </div>
    </dl>
  );
}

/**
 * Everything about the subscription on one screen: the agreement the customer
 * holds, and the catalogue they could hold instead.
 *
 * The subscription is read HERE and passed down rather than read twice.
 * {@link useSubscription} is not an ordinary query — it watches for the edge
 * where an unconfirmed subscription becomes a real one and invalidates the
 * whole application on it — so a second caller would be a second watcher, and
 * the two would invalidate the same key twice for one webhook.
 *
 * The cancel lives here too, one level above the card that offers it, because
 * the card does not always survive its own success: a cancel that ends an
 * unconfirmed agreement takes it out of the live set the read returns, and a
 * mutation owned by that card would unmount with it, taking the sentence
 * naming the date access ends with it.
 */
function PlanSection({
  applicationID,
  currency,
}: {
  applicationID: string;
  currency: string | undefined;
}) {
  const subscription = useSubscription(applicationID);
  const cancel = useCancelSubscription(applicationID, currency);
  const current = subscription.data?.subscription ?? null;

  return (
    <>
      {current || cancel.data ? (
        <SubscriptionCard
          applicationID={applicationID}
          subscription={current}
          cancel={cancel}
        />
      ) : null}
      <PlansCard
        applicationID={applicationID}
        currency={currency}
        current={current}
        unreadable={subscription.error}
        loading={subscription.isPending}
      />
    </>
  );
}

/**
 * The agreement itself: what it is, what state it is in, when its period ends,
 * which card pays for it, and the one action that stops it.
 *
 * It is a card of its own above the plans rather than a marked tile inside
 * them, because it answers a different question. The tiles are a CATALOGUE —
 * what could I be on — and this is the account. It is still the right thing to
 * render for a subscriber to a package that has since been archived and is on
 * sale to nobody, which a tile in a list of offers could not be.
 *
 * `subscription` is nullable so the card can outlive it by exactly one render:
 * see {@link PlanSection}.
 */
function SubscriptionCard({
  applicationID,
  subscription,
  cancel,
}: {
  applicationID: string;
  subscription: Subscription | null;
  cancel: ReturnType<typeof useCancelSubscription>;
}) {
  const [confirming, setConfirming] = useState(false);
  const refresh = useRefreshBilling(applicationID);
  const ending = Boolean(subscription?.cancel_at_period_end);

  return (
    <Card>
      <CardHeader
        title="Your plan"
        description="The subscription this wallet's monthly allowance comes from."
      />
      <CardBody className="space-y-5">
        {cancel.data ? <CancelledNotice result={cancel.data} /> : null}

        {cancel.error ? (
          <Notice tone="danger" title="We could not cancel this plan">
            {errorMessage(cancel.error)}
          </Notice>
        ) : null}

        {subscription ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-display text-lg font-bold tracking-[-0.01em] text-ink">
                  {subscription.package_name}
                </p>
                <p className="tnum mt-1 text-xs text-ink-3">
                  {formatMoney(subscription.price)} per{" "}
                  {intervalLabel(subscription.billing_interval, subscription.interval_count)} ·{" "}
                  {formatMoney(subscription.granted_credit)} of credit each period
                </p>
              </div>
              <StatusPill
                tone={statusTone(subscription)}
                dot
                pulse={subscription.status === "incomplete"}
              >
                {statusLabel(subscription)}
              </StatusPill>
            </div>

            {subscription.status === "past_due" ? (
              <PastDueNotice subscription={subscription} refresh={refresh} />
            ) : null}
            {ending ? <EndingNotice subscription={subscription} /> : null}
            <PendingNotice subscription={subscription} />

            <dl className="grid gap-3 border-t border-line-soft pt-4 text-xs sm:grid-cols-3">
              <Fact
                label={periodLabel(subscription)}
                value={
                  subscription.current_period_end
                    ? formatDate(subscription.current_period_end)
                    : "Not until the first payment"
                }
              />
              {/* A brand and four digits is the WHOLE of what this platform
                  holds about a card — the column is CHECKed to four characters
                  — so there is no fuller version of this line to leak. */}
              <Fact label="Paid with" value={cardLabel(subscription.payment_method) ?? "No card on file yet"} />
              <Fact label="Started" value={formatDate(subscription.created_at)} />
            </dl>

            {/* No cancel on a plan that is already ending. The route would
                accept it — a second cancel is a success that changes nothing —
                but a button whose only effect is to re-state a decision the
                page is already displaying invites the customer to wonder
                whether the first press worked. */}
            {ending ? null : (
              <div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setConfirming(true)}
                  disabled={cancel.isPending}
                >
                  {cancel.isPending ? "Cancelling…" : "Cancel this plan"}
                </Button>
              </div>
            )}

            <CancelDialog
              open={confirming}
              subscription={subscription}
              busy={cancel.isPending}
              onClose={() => setConfirming(false)}
              onConfirm={() => {
                setConfirming(false);
                cancel.mutate();
              }}
            />
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** One labelled fact in the card's summary row. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-2xs font-bold tracking-[0.07em] text-ink-4 uppercase">{label}</dt>
      <dd className="mt-1 font-semibold text-ink">{value}</dd>
    </div>
  );
}

/**
 * `Visa ···· 4242`, and there is no longer version of this string.
 *
 * A brand and four digits is everything the platform is allowed to know about
 * a card: the column is CHECKed to four characters and the provider is never
 * asked for more, so a full number cannot arrive here. The last four are taken
 * AGAIN anyway, because this is the one place in the portal that renders a
 * card and a `slice(-4)` is cheaper than trusting every future writer of that
 * column to have respected the check.
 */
function cardLabel(method: PaymentMethod | undefined): string | null {
  const brand = method?.brand?.trim() ?? "";
  const last4 = method?.last4?.trim() ?? "";
  if (!brand && !last4) {
    return null;
  }
  // The provider's vocabulary is lower case ("visa", "mastercard"), and a
  // sentence-cased brand is what every card on a statement looks like.
  const name = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card";
  return last4 ? `${name} ···· ${last4.slice(-4)}` : name;
}

/**
 * What the status is CALLED, which is not always what it is named.
 *
 * `incomplete` reads as "Pending" because that is what it is to the person
 * waiting — an agreement whose first payment nobody has confirmed — and
 * because the one thing it must never look like is a plan that is running.
 * `past_due` reads as the thing that happened rather than as jargon.
 *
 * A plan set to end outranks all of them: "Active" beside a notice saying the
 * plan stops on the 25th is the kind of contradiction a reader resolves by
 * distrusting the whole screen.
 */
function statusLabel(subscription: Subscription): string {
  if (subscription.cancel_at_period_end) {
    return "Ending";
  }
  switch (subscription.status) {
    case "incomplete":
      return "Pending";
    case "past_due":
      return "Payment failed";
    case "active":
      return "Active";
    default:
      return humanizeState(subscription.status);
  }
}

/**
 * The pill's colour. An ending plan is neutral rather than green — it is not a
 * fault and it is not a going concern — but an ending plan whose LAST PAYMENT
 * FAILED keeps its red: that one still needs the customer, because the credit
 * they are counting on until the period end may not be there.
 */
function statusTone(subscription: Subscription) {
  if (subscription.cancel_at_period_end && subscription.status === "active") {
    return "neutral" as const;
  }
  return subscriptionPillTone(subscription.status);
}

/** What the period end MEANS, which depends on what happens when it arrives. */
function periodLabel(subscription: Subscription): string {
  if (subscription.cancel_at_period_end) {
    return "Access ends";
  }
  if (subscription.status === "past_due") {
    return "Paid until";
  }
  return "Renews";
}

/**
 * A renewal the card refused.
 *
 * Distinct from every other state on this screen in tone and in wording,
 * because it is the only one that needs the customer to do something. It says
 * three things in order: the plan is still theirs, the credit they have
 * already been granted is still spendable and until when, and nothing new
 * arrives until a payment goes through. `past_due` is a LIVE status precisely
 * so the first two are true — losing the allowance the moment a card expires
 * would punish the customer for the bank's decision.
 *
 * THE ACTION IS TO LOOK AGAIN, AND IT IS LABELLED AS LOOKING AGAIN. The
 * provider owns the retry schedule and this platform records dunning rather
 * than driving it — there is no route that makes a card be tried sooner, and
 * no hosted page to send the customer to, because the failed invoice's URL is
 * not something any surface here carries. A button called "Retry payment" that
 * re-read a database would be a lie about whose money moved, so the copy names
 * the one recovery that does exist: end this plan and start it again on a card
 * that works.
 */
function PastDueNotice({
  subscription,
  refresh,
}: {
  subscription: Subscription;
  refresh: ReturnType<typeof useRefreshBilling>;
}) {
  const until = subscription.current_period_end;
  return (
    <Notice
      tone="danger"
      title="Your last payment was refused"
      icon={<TriangleAlert className="size-4" aria-hidden />}
    >
      <p>
        Your {subscription.package_name} plan is still yours, and the subscription credit you have
        already been granted stays spendable
        {until ? ` until ${formatDate(until)}` : ""}. No new credit is added until a payment goes
        through, and the payment provider retries the card on its own schedule over the next few
        days.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
        >
          {refresh.isPending ? "Checking…" : "Check the payment again"}
        </Button>
        {/* Only ever visible while the status is STILL past_due: a check that
            found the payment has landed unmounts this whole notice. */}
        {refresh.isSuccess ? (
          <span className="text-2xs text-ink-4">The payment has not gone through yet.</span>
        ) : null}
      </div>
      <p className="mt-3 text-2xs text-ink-4">
        If that card is no longer good, cancel this plan and start it again with another one. The
        credit above is yours either way, and your top-up credit is not affected.
      </p>
    </Notice>
  );
}

/**
 * A plan that has been cancelled and has not finished yet.
 *
 * It names the date twice over — here and in the summary beneath — because the
 * two are answering different questions: this one is "what did my cancel do",
 * and the row below is "when". It also says what happens to the credit at that
 * date, which is the fact a customer discovers on their own otherwise: the
 * subscription bucket is CLEARED at the reset, and only the top-up bucket
 * survives it.
 */
function EndingNotice({ subscription }: { subscription: Subscription }) {
  const ends = subscription.current_period_end;
  return (
    <Notice tone="warning" title="This plan is ending">
      {ends
        ? `It will not renew and nothing further will be charged. Your subscription credit stays
           spendable until ${formatDate(ends)}, and whatever is left of it is cleared then. Your
           top-up credit is not affected.`
        : `It will not renew and nothing further will be charged.`}
    </Notice>
  );
}

/**
 * What the cancel just did, said as a date.
 *
 * The date is the whole reason the route answers 200 with a body rather than
 * 204, so it is the whole reason this notice exists. Two endings are possible
 * and they are not the same event: an agreement with a paid period runs to the
 * end of it, and one whose hosted page was never completed stops immediately —
 * nothing was charged, so there is nothing to run out.
 *
 * `already_cancelling` is a success and is worded as one. The button that
 * produced it should not have been on screen, but a second cancel arriving
 * from a stale tab is not an error and must not read as one.
 */
function CancelledNotice({ result }: { result: CancelSubscriptionResult }) {
  const ends = result.access_ends_at;
  return (
    <Notice
      tone={ends ? "info" : "warning"}
      title={result.already_cancelling ? "This plan was already ending" : "Your plan is cancelled"}
    >
      {ends
        ? `Nothing further will be charged. You can keep spending your subscription credit until
           ${formatDate(ends)}, and whatever is left of it is cleared then.`
        : `Nothing was charged and it ended straight away — no payment had been taken, so there was
           no period to run out. You can start a plan again below.`}
    </Notice>
  );
}

/**
 * The confirmation.
 *
 * Cancelling is not undoable in the way the word suggests: resubscribing
 * writes a NEW agreement rather than reviving this one, and it starts a new
 * period at whatever the plan costs today. So the press is confirmed, and the
 * dialog spends its three lines on the three things a customer is actually
 * unsure about — what happens to the credit they have already been granted,
 * what happens to the credit they BOUGHT, and whether they can come back.
 */
function CancelDialog({
  open,
  subscription,
  busy,
  onClose,
  onConfirm,
}: {
  open: boolean;
  subscription: Subscription;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const ends = subscription.current_period_end;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Cancel ${subscription.package_name}?`}
      description="This stops the plan renewing. It does not take back credit you have already been granted."
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Keep the plan
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm} disabled={busy}>
            {busy ? "Cancelling…" : "Cancel the plan"}
          </Button>
        </>
      }
    >
      <ul className="list-disc space-y-2 pl-4 text-xs leading-relaxed text-ink-3">
        <li>
          {ends
            ? `Your subscription credit stays spendable until ${formatDate(ends)}. Whatever is left
               of it is cleared then.`
            : "No payment has been taken, so the plan ends straight away."}
        </li>
        <li>Your top-up credit is not affected. It was bought outright and never expires.</li>
        <li>You can start a plan again afterwards, at the price it costs then.</li>
      </ul>
    </Modal>
  );
}

/**
 * The plans on sale for this application, and the one action that starts one.
 *
 * It sits directly under the balance because that is the question it answers:
 * a customer looking at what is left is the one deciding whether to buy more,
 * and a subscription is the other way of doing what the panel beside it does.
 * Both remain available at once — a subscription grants an allowance that
 * resets, a top-up buys credit outright, and an account can hold both.
 *
 * The whole card is absent for a customer with no wallet for this application:
 * the plans route answers 404 there, exactly as the wallet read does, and the
 * balance card above is already saying so in words. Rendering an empty plans
 * card beneath it would be a second, vaguer version of the same message.
 */
function PlansCard({
  applicationID,
  currency,
  current,
  unreadable,
  loading,
}: {
  applicationID: string;
  currency: string | undefined;
  current: Subscription | null;
  unreadable: unknown;
  loading: boolean;
}) {
  const packages = usePackages(applicationID);
  const subscribe = useStartSubscription(applicationID);

  const offers = packages.data?.packages ?? [];

  if (packages.isPending || loading) {
    return <Skeleton className="h-56 w-full" />;
  }
  // 404 from the plans route: no wallet for this application. See above.
  if (!packages.error && packages.data === null) {
    return null;
  }

  const start = (offer: PackageOffer) => {
    subscribe.mutate({
      package_id: offer.id,
      // The wallet's own currency, for the reason the top-up sends it: a
      // subscription is pinned to one wallet, and letting the platform default
      // decide would open a second wallet in another currency.
      currency,
      // A fresh key per press, exactly as the top-up mints one. The key stops a
      // retried REQUEST from opening a second agreement; reusing it across two
      // deliberate presses would instead hand back the first plan's page.
      idempotency_key: crypto.randomUUID(),
      success_url: returnURL(applicationID, "subscribe", "paid"),
      cancel_url: returnURL(applicationID, "subscribe", "cancelled"),
    });
  };

  return (
    <Card>
      <CardHeader
        title="Plans"
        description="A subscription grants credit every period. Top-ups stay available either way."
      />
      <CardBody className="space-y-5">
        {packages.error ? (
          <Notice tone="danger" title="We could not load the plans">
            {errorMessage(packages.error)}
          </Notice>
        ) : null}

        {/* A failed subscription read is not a reason to hide the plans, but it
            IS a reason to offer no action: without it there is no way to know
            which plan is already held, and a subscribe that collides with a
            live one is refused by the platform anyway. */}
        {unreadable ? (
          <Notice tone="danger" title="We could not read your current plan">
            {errorMessage(unreadable)}
          </Notice>
        ) : null}

        {subscribe.error ? (
          <Notice tone="danger" title="We could not open a payment page">
            {errorMessage(subscribe.error)}
          </Notice>
        ) : null}

        {subscribe.data ? <SubscribeNotice result={subscribe.data} /> : null}

        {offers.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<Layers className="size-5" aria-hidden />}
            title="No plans on sale"
            body="Nothing is being offered for this application yet. Top-ups still work."
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {offers.map((offer) => (
              <PlanTile
                key={offer.id}
                offer={offer}
                subscription={current}
                readable={!unreadable}
                busy={subscribe.isPending && subscribe.variables?.package_id === offer.id}
                disabled={subscribe.isPending}
                onSubscribe={() => start(offer)}
              />
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * The state between pressing pay and the money being real.
 *
 * `incomplete` is the honest word for it: the agreement exists, the hosted page
 * may well have been completed, and nothing has been granted. Only the
 * provider's webhook decides, so this says "we are waiting" rather than
 * congratulating anybody, and {@link useSubscription} re-reads on a timer so
 * the wait ends without the customer being told to refresh.
 *
 * Past the polling window the copy changes rather than the banner vanishing.
 * An abandoned hosted page leaves exactly this row behind, and a customer who
 * closed the tab needs to be told to finish it — not shown a spinner that has
 * quietly given up.
 */
function PendingNotice({ subscription }: { subscription: Subscription | null }) {
  if (subscription?.status !== "incomplete") {
    return null;
  }
  const waiting = pendingPoll(subscription) !== false;
  return (
    <Notice
      tone="warning"
      title={waiting ? "Confirming your payment" : "This subscription is still unconfirmed"}
    >
      {waiting
        ? `Your ${subscription.package_name} plan starts as soon as the payment provider confirms
           the first payment. This page checks for you — nothing to refresh.`
        : `We have not heard back from the payment provider about your ${subscription.package_name}
           plan. If you closed the payment page before finishing, start the plan again below.`}
    </Notice>
  );
}

/**
 * The hosted page, rendered as something the customer presses.
 *
 * Identical in kind to {@link CheckoutNotice}, and for the identical reason:
 * the portal does not navigate anywhere on the strength of a URL in a response
 * body, even one its own platform sent. It is also the only thing that could
 * work — the URL arrives from an awaited request, by which time the press that
 * asked for it is over and a pop-up would be blocked.
 */
function SubscribeNotice({ result }: { result: SubscribeResult }) {
  return (
    <Notice
      tone="success"
      title={result.replayed ? "This subscription is already open" : "Your payment page is ready"}
    >
      <p className="tnum font-semibold text-ink">
        {formatMoney(result.checkout.price)} per{" "}
        {intervalLabel(result.subscription.billing_interval, result.subscription.interval_count)}{" "}
        for {result.subscription.package_name}
        {result.replayed ? " — no second subscription was opened." : "."}
      </p>
      <p className="mt-2">
        <a
          href={result.checkout.url}
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-semibold text-brand hover:text-brand-strong"
        >
          Open the secure payment page
          <ExternalLink className="size-3.5" aria-hidden />
        </a>{" "}
        <span className="text-ink-3">
          — it expires {formatTimestamp(result.checkout.expires_at)}.
        </span>
      </p>
      <p className="mt-2 text-2xs text-ink-4">
        Your plan is pending until the payment provider confirms the payment. Nothing is granted
        before then.
      </p>
    </Notice>
  );
}

/** `month`, or `3 months` — what one billing period is called. */
function intervalLabel(interval: "month" | "year", count: number): string {
  return count === 1 ? interval : `${count} ${interval}s`;
}

/**
 * One plan.
 *
 * The extra is printed as an AMOUNT and a percentage, because the two answer
 * different questions — "how much more do I get" and "is this a better deal
 * than the plan above" — and both come from the platform already computed. The
 * browser does no money arithmetic: `extra` is a Money and `extra_bps` is the
 * same fact in basis points, truncated toward zero so a plan is never
 * advertised as better than it is.
 *
 * A plan the customer already holds is MARKED and offers nothing. The others
 * offer a subscribe only while there is no live subscription at all: the
 * platform refuses a second one for the same application (subscriptions_live_key
 * is underneath it), so a button there would be an action that exists only to
 * be rejected. Switching plans is a cancel and then a subscribe, and the tile
 * says so.
 */
function PlanTile({
  offer,
  subscription,
  readable,
  busy,
  disabled,
  onSubscribe,
}: {
  offer: PackageOffer;
  subscription: Subscription | null;
  readable: boolean;
  busy: boolean;
  disabled: boolean;
  onSubscribe: () => void;
}) {
  const mine = subscription?.package_id === offer.id;
  const period = intervalLabel(offer.billing_interval, offer.interval_count);
  const bonus = micros(offer.extra) > 0n;

  return (
    <div
      className={cn(
        "flex flex-col rounded-card border p-4",
        mine ? "border-brand bg-brand-softer/40" : "border-line bg-surface",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="font-display text-sm font-bold text-ink">{offer.name}</p>
        {mine && subscription ? (
          <StatusPill
            tone={subscription.status === "incomplete" ? "warning" : "brand"}
            dot
            pulse={subscription.status === "incomplete"}
          >
            {subscription.status === "incomplete" ? "Pending" : "Your plan"}
          </StatusPill>
        ) : null}
      </div>

      {offer.description ? (
        <p className="mt-1 text-xs leading-relaxed text-ink-4">{offer.description}</p>
      ) : null}

      <p className="tnum mt-4 font-display text-2xl font-bold tracking-[-0.02em] text-ink">
        {formatMoney(offer.price)}
        <span className="text-sm font-semibold text-ink-4"> / {period}</span>
      </p>
      <p className="mt-1 text-xs text-ink-3">
        {formatMoney(offer.granted_credit)} of credit every {period}
      </p>
      {bonus ? (
        <p className="mt-2 text-xs font-semibold text-success-ink">
          {formatMoney(offer.extra)} more than a top-up — {formatBps(offer.extra_bps)} extra credit
        </p>
      ) : null}

      <div className="mt-4 flex grow items-end">
        {mine ? (
          <p className="text-2xs text-ink-4">
            {subscription?.status === "incomplete"
              ? "Waiting for the payment provider."
              : "This is the plan you are on."}
          </p>
        ) : !readable ? (
          <p className="text-2xs text-ink-4">Reload to subscribe.</p>
        ) : subscription ? (
          <p className="text-2xs text-ink-4">
            Cancel your current plan before starting this one.
          </p>
        ) : (
          <Button size="sm" onClick={onSubscribe} disabled={disabled}>
            {busy ? "Opening…" : `Subscribe — ${formatMoney(offer.price)}`}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Amounts are entered in MAJOR units and sent in MINOR units, converted by
 * string surgery rather than by multiplying a float.
 *
 * `25.10 * 100` is 2509.9999999999995 in IEEE-754, and a top-up that charges a
 * cent less than the customer typed is the kind of defect nobody finds for
 * months. Two decimal places is the exponent for every currency this build
 * prices in; a zero-exponent currency (JPY) would need the exponent from the
 * platform, which the wire does not carry yet.
 */
const AMOUNT_PATTERN = /^\d{1,6}(\.\d{1,2})?$/;

const topUpSchema = z.object({
  amount: z
    .string()
    .trim()
    .regex(AMOUNT_PATTERN, "Enter an amount like 25 or 25.50.")
    .refine((value) => minorUnits(value) > 0, "Enter an amount greater than zero."),
});

type TopUpValues = z.infer<typeof topUpSchema>;

export function minorUnits(major: string): number {
  const [whole = "0", fraction = ""] = major.trim().split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
}

/**
 * The design's "Add credit" panel, driven by the real checkout.
 *
 * `AddCreditPanel` is a CONTROLLED component — it owns no amount of its own —
 * so the field stays in the same zod form the screen has always validated
 * with, reached through `watch`/`setValue` instead of `register`. That keeps
 * one description of a valid amount: the panel's own `topUpAmountError` knows
 * a ceiling and a sign, but only {@link topUpSchema} knows that three decimal
 * places is not an amount this wire can carry.
 */
function TopUpPanel({
  application,
  wallet,
}: {
  application: PortalApplication;
  wallet: WalletPosition | null | undefined;
}) {
  const form = useZodForm<TopUpValues>(topUpSchema, { defaultValues: { amount: "25" } });
  const topUp = useStartTopUp();
  const checkout = topUp.data;
  const amount = form.watch("amount");

  const submit = form.handleSubmit((values) => {
    topUp.mutate({
      application_id: application.id,
      amount_minor: minorUnits(values.amount),
      // Omitted currency means the platform default, which is also what the
      // wallet reads back as. Sending the wallet's currency when there is one
      // keeps a second wallet from being opened in a different one.
      currency: wallet?.currency,
      // A fresh key per press. The key exists so a retried REQUEST cannot
      // charge twice; reusing it across two deliberate top-ups would instead
      // hand back the first payment page and silently swallow the second.
      idempotency_key: crypto.randomUUID(),
      description: `Top-up for ${application.name}`,
      success_url: returnURL(application.id, "topup", "paid"),
      cancel_url: returnURL(application.id, "topup", "cancelled"),
    });
  });

  return (
    <div className="space-y-5">
      <AddCreditPanel
        appName={application.name}
        amount={amount}
        onAmountChange={(next) =>
          form.setValue("amount", next, { shouldValidate: true, shouldDirty: true })
        }
        onSubmit={() => void submit()}
        // Never "done": the panel's done state disables its own button, and a
        // customer who opened a checkout and changed their mind about the
        // amount would have no way to open a second one.
        status={topUp.isPending ? "loading" : "idle"}
        submitError={form.formState.errors.amount?.message}
        currency={wallet?.currency ?? "USD"}
      />

      {topUp.error ? (
        <Notice tone="danger" title="We could not open a payment page">
          {errorMessage(topUp.error)}
        </Notice>
      ) : null}

      {checkout ? <CheckoutNotice checkout={checkout} application={application} /> : null}
    </div>
  );
}

/**
 * The payment page, rendered as something the customer presses.
 *
 * The portal does not navigate anywhere on the strength of a URL in a response
 * body, even one its own platform sent — the same rule the authorize screen
 * follows for a `redirect_uri`.
 */
function CheckoutNotice({
  checkout,
  application,
}: {
  checkout: CheckoutResult;
  application: PortalApplication;
}) {
  return (
    <Notice
      tone="success"
      title={checkout.replayed ? "This payment is already open" : "Your payment page is ready"}
    >
      <p className="tnum font-semibold text-ink">
        {formatMoney(checkout.checkout.amount)} for {application.name}
        {checkout.replayed ? " — no second payment was opened." : "."}
      </p>
      <p className="mt-2">
        <a
          href={checkout.checkout.url}
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-semibold text-brand hover:text-brand-strong"
        >
          Open the secure payment page
          <ExternalLink className="size-3.5" aria-hidden />
        </a>{" "}
        <span className="text-ink-3">
          — it expires {formatTimestamp(checkout.checkout.expires_at)}.
        </span>
      </p>
      <p className="mt-2 text-2xs text-ink-4">
        Credit lands in this wallet once the payment provider confirms it, which is normally a few
        seconds after you pay.
      </p>
    </Notice>
  );
}

/** Where the payment provider sends the browser back to. Absolute, because
 *  both checkout routes reject a relative one — they are handing the URL to a
 *  third party, which has no origin to resolve it against.
 *
 *  `flow` is the search parameter to come back under, so a top-up and a
 *  subscribe land on the same screen saying different things. */
function returnURL(applicationID: string, flow: "topup" | "subscribe", outcome: string): string {
  return `${window.location.origin}/wallets/${applicationID}?${flow}=${outcome}`;
}

/**
 * A table that is still loading is a skeleton the size of a table, not a
 * spinner: the card keeps its height, so the three below it do not jump up the
 * page and then back down as each read lands.
 */
function TableCard({
  title,
  description,
  caption,
  error,
  errorTitle,
  pending,
  children,
}: {
  title: string;
  description?: string;
  caption: string;
  error: unknown;
  errorTitle: string;
  pending: boolean;
  children: ReactNode;
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader title={title} description={description} />
      {error ? (
        <CardBody>
          <Notice tone="danger" title={errorTitle}>
            {errorMessage(error)}
          </Notice>
        </CardBody>
      ) : pending ? (
        <CardBody>
          <Skeleton className="h-24 w-full" />
          <span className="sr-only">{`Loading ${caption.toLowerCase()}`}</span>
        </CardBody>
      ) : (
        children
      )}
    </Card>
  );
}

function TransactionsCard({ applicationID, name }: { applicationID: string; name: string }) {
  const transactions = useTransactions(applicationID);
  return (
    <TableCard
      title="Transactions"
      description="Newest first."
      caption={`Transactions for ${name}`}
      error={transactions.error}
      errorTitle="We could not load your transactions"
      pending={transactions.isPending}
    >
      <DataTable
        caption={`Transactions for ${name}`}
        columns={transactionColumns}
        rows={transactions.data?.transactions ?? []}
        empty={
          <EmptyState
            size="sm"
            title="No transactions yet"
            body="Top-ups and charges against this wallet will be listed here."
          />
        }
      />
    </TableCard>
  );
}

/**
 * A movement's tone is read off the money, not off the kind.
 *
 * There are nine `TransactionKind`s and more will be added; colouring by a
 * list of names means the next one added upstream renders as "nothing
 * happened". The sign of the available delta is the thing a customer is
 * actually looking for in this column and it cannot go stale.
 */
function movementTone(row: Transaction) {
  return micros(row.available_delta) > 0n ? "success" : "neutral";
}

function micros(amount: Money): bigint {
  return BigInt(amount.micros);
}

/** A delta, signed and coloured: credit arriving reads differently from credit
 *  leaving, which is the whole reason to scan this column. */
function Delta({ amount }: { amount: Money }) {
  const value = micros(amount);
  return (
    <span className={cn("tnum font-semibold", value > 0n ? "text-success-ink" : "text-ink")}>
      {value > 0n ? "+" : ""}
      {formatMoney(amount)}
    </span>
  );
}

const transactionColumns: Column<Transaction>[] = [
  {
    key: "when",
    header: "When",
    className: "w-[13rem]",
    cell: (row) => (
      <span className="tnum whitespace-nowrap">{formatTimestamp(row.occurred_at)}</span>
    ),
  },
  {
    key: "kind",
    header: "Kind",
    className: "w-40",
    cell: (row) => (
      <StatusPill tone={movementTone(row)}>{row.kind.replaceAll("_", " ")}</StatusPill>
    ),
  },
  {
    key: "description",
    header: "Description",
    cell: (row) => <span className="text-ink-2">{row.description ?? "—"}</span>,
  },
  {
    key: "available",
    header: "Available",
    align: "right",
    className: "w-36",
    cell: (row) => <Delta amount={row.available_delta} />,
  },
  {
    // Both deltas are shown because a reservation changes only the second one.
    // A single "amount" column would render a hold as a no-op and leave the
    // customer wondering where their credit went.
    key: "held",
    header: "Held",
    align: "right",
    className: "w-32",
    cell: (row) => <Delta amount={row.reserved_delta} />,
  },
];

function InvoicesCard({ applicationID, name }: { applicationID: string; name: string }) {
  const invoices = useInvoices(applicationID);
  return (
    <TableCard
      title="Top-up receipts"
      caption={`Top-up receipts for ${name}`}
      error={invoices.error}
      errorTitle="We could not load your receipts"
      pending={invoices.isPending}
    >
      <DataTable
        caption={`Top-up receipts for ${name}`}
        columns={invoiceColumns}
        rows={invoices.data?.invoices ?? []}
        empty={
          <EmptyState
            size="sm"
            icon={<Receipt className="size-5" aria-hidden />}
            title="No receipts yet"
            body="Add credit and your receipt will appear here."
          />
        }
      />
    </TableCard>
  );
}

const invoiceColumns: Column<Invoice>[] = [
  {
    key: "when",
    header: "When",
    className: "w-[13rem]",
    cell: (row) => (
      <span className="tnum whitespace-nowrap">{formatTimestamp(row.created_at)}</span>
    ),
  },
  {
    key: "amount",
    header: "Amount",
    className: "w-36",
    cell: (row) => (
      <span className="tnum font-semibold whitespace-nowrap text-ink">
        {formatMoney(row.amount)}
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    className: "w-32",
    cell: (row) => <StatusPill tone={topUpPillTone(row.status)}>{row.status}</StatusPill>,
  },
  {
    key: "paid with",
    header: "Paid with",
    className: "w-40",
    cell: (row) => <code className="font-mono text-2xs text-ink-3">{row.provider}</code>,
  },
  {
    // A payment that failed is the row a customer came here to understand, so
    // the reason is a column rather than something behind a click.
    key: "detail",
    header: "Detail",
    className: "w-44",
    cell: (row) =>
      row.failure_reason ? (
        <span className="text-danger-ink">{row.failure_reason}</span>
      ) : row.status === "pending" && row.checkout_url ? (
        <a
          href={row.checkout_url}
          rel="noreferrer"
          className="font-semibold whitespace-nowrap text-brand hover:text-brand-strong"
        >
          Finish this payment
        </a>
      ) : (
        <span className="text-ink-4">—</span>
      ),
  },
];

function UsageCard({ applicationID, name }: { applicationID: string; name: string }) {
  const usage = useUsage(applicationID);
  return (
    <TableCard
      title="What it was spent on"
      description={`Last ${RECENT_LIMIT} priced requests.`}
      caption={`Priced requests for ${name}`}
      error={usage.error}
      errorTitle="We could not load this application's usage"
      pending={usage.isPending}
    >
      <DataTable
        caption={`Priced requests for ${name}`}
        columns={usageColumns}
        rows={usage.data?.usage ?? []}
        empty={
          <EmptyState
            size="sm"
            title="No requests yet"
            body="Nothing has been priced against this wallet yet."
          />
        }
      />
    </TableCard>
  );
}

const usageColumns: Column<UsageRecord>[] = [
  {
    key: "when",
    header: "When",
    className: "w-[13rem]",
    cell: (row) => (
      <span className="tnum whitespace-nowrap">{formatTimestamp(row.created_at)}</span>
    ),
  },
  {
    key: "model",
    header: "Model",
    cell: (row) => (
      <code className="font-mono text-xs font-semibold text-ink">
        {row.model ?? row.provider ?? "—"}
      </code>
    ),
  },
  {
    key: "status",
    header: "Status",
    className: "w-28",
    cell: (row) => <StatusPill tone={reservationPillTone(row.status)}>{row.status}</StatusPill>,
  },
  {
    key: "held",
    header: "Held",
    align: "right",
    className: "w-32",
    cell: (row) => <span className="tnum text-ink-3">{formatMoney(row.reserved)}</span>,
  },
  {
    key: "charged",
    header: "Charged",
    align: "right",
    className: "w-32",
    cell: (row) => (
      <span className="tnum font-semibold text-ink">
        {row.settled ? formatMoney(row.settled) : "—"}
      </span>
    ),
  },
];
